import { SupabaseClient } from '@supabase/supabase-js';
import { mapNotificationDtoToNotification } from 'modules/core/domain/mappers/notification-data-mappers';
import {
  CreateNotificationProps,
  Notification,
  NotificationDto,
  NotificationFilter,
  UpdateNotificationProps,
} from '../../domain/interfaces/supabase/notifications/types';

export default class Notifications {
  constructor(private supabase: SupabaseClient) {}

  async getNotifications(filter?: NotificationFilter): Promise<{ data: Notification[]; count: number }> {
    let query = this.supabase.from('notifications').select('*', { count: 'exact' });

    if (filter?.type?.length && filter.type !== 'all') {
      if (filter.type === 'user_event' && filter.userId) {
        query = query.eq('user_id', filter.userId);
      } else {
        query = query.eq('type', filter.type);
      }
    } else {
      if (filter?.userId === undefined) {
        query = query.is('user_id', null);
      } else {
        query = query.or(`user_id.eq.${filter.userId},user_id.is.null`);
      }
    }

    if (filter?.sort) {
      query = query.order('created_at', {
        ascending: filter.sort === 'asc',
      });
    } else {
      // По умолчанию сортируем по дате создания (сначала новые)
      query = query.order('created_at', { ascending: false });
    }

    if (filter?.limit) {
      query = query.limit(filter.limit);
    }

    if (filter?.offset) {
      query = query.range(filter.offset, filter.offset + (filter.limit || 10) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }

    return {
      data: data.map(mapNotificationDtoToNotification),
      count: count || 0,
    };
  }

  async getNotificationById(id: number): Promise<Notification> {
    const { data, error } = await this.supabase.from('notifications').select('*').eq('id', id).single();

    if (error) {
      console.error('Error fetching notification:', error);
      throw error;
    }

    return mapNotificationDtoToNotification(data as NotificationDto);
  }

  async createNotification(props: CreateNotificationProps): Promise<Notification> {
    const { data, error } = await this.supabase
      .from('notifications')
      .insert({
        type: props.type,
        title: props.title,
        text: props.text,
        user_id: props.userId ?? null,
        order: props.order ?? 0,
        meta: props.meta ?? {},
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating notification:', error);
      throw error;
    }

    return mapNotificationDtoToNotification(data as NotificationDto);
  }

  async updateNotification(props: UpdateNotificationProps): Promise<Notification> {
    const updates: Partial<NotificationDto> = {};

    if (props.type !== undefined) updates.type = props.type;
    if (props.title !== undefined) updates.title = props.title;
    if (props.text !== undefined) updates.text = props.text;
    if (props.userId !== undefined) updates.user_id = props.userId;
    if (props.order !== undefined) updates.order = props.order;
    if (props.meta !== undefined) updates.meta = props.meta;

    const { data, error } = await this.supabase.from('notifications').update(updates).eq('id', props.id).select().single();

    if (error) {
      console.error('Error updating notification:', error);
      throw error;
    }

    return mapNotificationDtoToNotification(data as NotificationDto);
  }

  async deleteNotification(id: number): Promise<void> {
    const { error } = await this.supabase.from('notifications').delete().eq('id', id);

    if (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }
}
